<template>
  <div>
    <!-- The ticket requires unfound fields to read as empty rather than be guessed at. -->
    <v-expansion-panels v-if="missingFields.length > 0" variant="accordion" class="mt-2">
      <v-expansion-panel>
        <v-expansion-panel-title class="text-body-2">
          <v-icon icon="mdi-alert-circle-outline" size="18" class="mr-2"></v-icon>
          {{ missingFields.length }} {{ missingFields.length === 1 ? "field was" : "fields were" }} not found in the
          documents
        </v-expansion-panel-title>
        <v-expansion-panel-text>
          <div class="text-body-2 text-medium-emphasis mb-2">
            Nothing was read for these — fill them in by hand, or upload a document that shows them and run autofill
            again.
          </div>
          <v-chip v-for="field in missingFields" :key="field.key" size="small" variant="outlined" class="mr-2 mb-2">
            {{ field.label }}
          </v-chip>
        </v-expansion-panel-text>
      </v-expansion-panel>
    </v-expansion-panels>
  </div>
</template>

<script setup>
import { computed } from "vue";

// Field keys match orderInfo's own keys. The backend sends snake_case; the axios interceptor
// camelizes it. Field candidates are shown under their own inputs (AutofillFieldChoices); this list
// only drives the "not found" summary. Mirrors AutofillField in portal/logic/ai/create_submission_autofill.py.
const SECTIONS = [
  {
    title: "Patient",
    fields: [
      { key: "patientFirstName", label: "First name" },
      { key: "patientLastName", label: "Last name" },
      { key: "patientDob", label: "Date of birth" },
      { key: "patientGender", label: "Gender" },
      { key: "patientPhone", label: "Phone" },
      { key: "patientAddress1", label: "Address line 1" },
      { key: "patientCity", label: "City" },
      { key: "patientState", label: "State" },
      { key: "patientZip", label: "ZIP" },
    ],
  },
  {
    title: "Insurance",
    fields: [
      { key: "insuranceProvider", label: "Insurance provider" },
      { key: "patientMemberId", label: "Member ID" },
      { key: "benefitType", label: "Benefit category" },
      { key: "bin", label: "Rx BIN" },
      { key: "processorControlNumber", label: "Rx PCN" },
      { key: "grpNumber", label: "Rx group number" },
    ],
  },
  {
    title: "Provider",
    fields: [
      { key: "providerFirstName", label: "First name" },
      { key: "providerLastName", label: "Last name" },
      { key: "providerNpiNumber", label: "NPI" },
      { key: "providerContactPhone", label: "Contact phone" },
      { key: "providerContactFax", label: "Contact fax" },
      { key: "providerAddress1", label: "Address line 1" },
      { key: "providerCity", label: "City" },
      { key: "providerState", label: "State" },
      { key: "providerZip", label: "ZIP" },
    ],
  },
  {
    title: "Prescription",
    fields: [
      { key: "medication", label: "Medication" },
      { key: "nationalDrugCode", label: "NDC" },
      { key: "dailyDosage", label: "Daily dosage" },
      { key: "frequency", label: "Frequency" },
      { key: "packageSizeUnit", label: "Package size unit" },
      { key: "quantityPer30Days", label: "Quantity per 30 days" },
      { key: "expectedLengthOfTherapy", label: "Duration of therapy" },
    ],
  },
];

// Diagnoses are reported separately from SECTIONS (they are pairs, and there can be several),
// so they get their own entry in the not-found list.
const DIAGNOSIS_MISSING_LABEL = "Diagnosis: primary diagnosis and ICD-10";

const props = defineProps({
  // { orderInfoFieldKey: [{ value, sourceDocument, sourcePage, confidence, evidence }] }
  candidates: {
    type: Object,
    required: true,
  },
  // Diagnosis candidates; only their count matters here (feeds the not-found list).
  diagnoses: {
    type: Array,
    default() {
      return [];
    },
  },
});

const hasCandidates = (key) => Array.isArray(props.candidates[key]) && props.candidates[key].length > 0;

const missingFields = computed(() => {
  const missing = SECTIONS.flatMap((section) =>
    section.fields
      .filter((f) => !hasCandidates(f.key))
      .map((f) => ({ key: f.key, label: `${section.title}: ${f.label}` }))
  );
  if (props.diagnoses.length === 0) {
    missing.push({ key: "diagnoses", label: DIAGNOSIS_MISSING_LABEL });
  }
  return missing;
});
</script>
